import { useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Spinner } from '../../components/atoms/Spinner'
import { Paginator } from '../../components/molecules/Paginator'
import { TableShell } from '../../components/molecules/TableShell'
import { Th, Td } from '../../components/molecules/Table'
import { SolutionFormModal } from './SolutionFormModal'
import {
  useSolutions,
  type SolutionDto
} from '../../../viewmodels/useSolutions'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'

/**
 * /products/solutions — Solutions operator screen.
 *
 * Lists customer-facing bundles. Each row carries the derived selling
 * price (sum of variant prices × qty); the column shows a "—" when
 * any line variant is missing a current price, since the operator
 * should fix Variant Pricing before the solution can be priced.
 *
 * v1 ships single-hero-image only — multi-image gallery is a later
 * slice (schema already supports it).
 */
export function SolutionsPage(): JSX.Element {
  const vm = useSolutions()
  const { can } = useAuth()
  const canAdd = can(SCREENCODES.PRODUCTSOLUTIONS, 'add')
  const canEdit = can(SCREENCODES.PRODUCTSOLUTIONS, 'edit')
  const canDelete = can(SCREENCODES.PRODUCTSOLUTIONS, 'delete')

  const [addOpen, setAddOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<SolutionDto | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

  const handleDelete = async (row: SolutionDto): Promise<void> => {
    if (!window.confirm(`Delete "${row.name}"? It will be removed from the catalog.`)) return
    setActionError(null)
    setPendingDeleteId(row.id)
    const res = await vm.remove(row.id)
    setPendingDeleteId(null)
    if (!res.ok) setActionError(res.message)
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-h2 text-charcoal">Solutions</h1>
          <p className="font-body text-body-sm text-text-secondary mt-1">
            Customer-facing bundles of product variants. Price is
            derived from each line — fix Variant Pricing first if a
            row shows "—".
          </p>
        </div>
        {canAdd ? <Button onClick={() => setAddOpen(true)}>Add solution</Button> : null}
      </header>

      {vm.error !== null ? (
        <Alert tone="error">
          {vm.error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={vm.reload}
          >
            Retry
          </button>
        </Alert>
      ) : null}
      {actionError !== null ? <Alert tone="error">{actionError}</Alert> : null}
      {vm.pickerError !== null ? (
        <Alert tone="warning">Variant picker failed to load: {vm.pickerError}</Alert>
      ) : null}

      {vm.isLoading && vm.items.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : vm.items.length === 0 ? (
        <Placeholder>No solutions yet.</Placeholder>
      ) : (
        <>
          <TableShell>
            <table className="w-full font-body text-body-sm">
              <thead className="bg-parchment/50 text-text-secondary">
                <tr>
                  <Th className="w-16">Image</Th>
                  <Th>Name</Th>
                  <Th>Description</Th>
                  <Th className="text-right">Variants</Th>
                  <Th className="text-right">Price</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {vm.items.map((row) => (
                  <tr key={row.id} className="border-t border-border align-middle">
                    <Td>
                      {row.thumbnail_url !== null ? (
                        <img
                          src={row.thumbnail_url}
                          alt={row.name}
                          className="w-12 h-12 rounded object-cover bg-parchment/40"
                          crossOrigin="use-credentials"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded bg-parchment/40 flex items-center justify-center font-mono text-body-xs text-text-secondary">
                          —
                        </div>
                      )}
                    </Td>
                    <Td className="text-charcoal">{row.name}</Td>
                    <Td className="max-w-[24rem] truncate" title={row.description}>
                      {row.description}
                    </Td>
                    <Td className="text-right font-mono">{row.active_variant_count}</Td>
                    <Td className="text-right font-mono">
                      {row.derived_selling_price_paisa !== null
                        ? formatRupees(row.derived_selling_price_paisa)
                        : <span className="text-text-secondary">—</span>}
                    </Td>
                    <Td className="text-right whitespace-nowrap">
                      {canEdit ? (
                        <Button size="sm" variant="secondary" onClick={() => setEditTarget(row)}>
                          Edit
                        </Button>
                      ) : null}
                      {canDelete ? (
                        <>
                          {' '}
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={pendingDeleteId === row.id}
                            onClick={() => void handleDelete(row)}
                          >
                            {pendingDeleteId === row.id ? 'Deleting…' : 'Delete'}
                          </Button>
                        </>
                      ) : null}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableShell>
          <Paginator
            page={vm.page}
            pageSize={vm.pageSize}
            total={vm.total}
            onPageChange={vm.setPage}
            disabled={vm.isLoading}
          />
        </>
      )}

      {addOpen ? (
        <SolutionFormModal
          open={addOpen}
          mode="create"
          initial={null}
          variantPicker={vm.variantPicker}
          onClose={() => setAddOpen(false)}
          onSubmit={async (input) => {
            if (input.kind === 'create') {
              return vm.create(input.payload)
            }
            return { ok: false, field: null, message: 'Unexpected mode' }
          }}
        />
      ) : null}

      {editTarget !== null ? (
        <SolutionFormModal
          open={editTarget !== null}
          mode="edit"
          initial={editTarget}
          variantPicker={vm.variantPicker}
          onClose={() => setEditTarget(null)}
          onSubmit={async (input) => {
            if (input.kind === 'update' && editTarget !== null) {
              return vm.update(editTarget.id, input.payload)
            }
            return { ok: false, field: null, message: 'Unexpected mode' }
          }}
        />
      ) : null}
    </div>
  )
}

function formatRupees(paisa: number): string {
  return `₹${(paisa / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function Placeholder({ children }: { readonly children: React.ReactNode }): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-body text-body-sm text-text-secondary">{children}</p>
    </div>
  )
}
